import * as styles from "./Footer.css";
import { ComponentPropsWithRef } from "react";
import { MarqueeText } from "../elements/MarqueeText";
import { Exclude } from "~/assets/svgs/Exclude";
import Grid from "./Grid";
import { BrandMarkNorthAmerica } from "~/assets/svgs/BrandMarkNorthAmerica";
import { Contact, Link as ILink, Menu, Settings } from "~/sanity/types";
import { ButtonLink } from "../elements/ButtonLink";
import { Paragraph } from "../elements/Paragraph";
import Link from "../elements/Link";

// @ts-ignore
import Heart from "@material-design-icons/svg/outlined/favorite_border.svg?react";
// @ts-ignore
import Phone from "@material-design-icons/svg/outlined/phone.svg?react";
// @ts-ignore
import Send from "@material-design-icons/svg/outlined/send.svg?react";
import { Socials } from "../elements/Socials";
import PortableText, { IPortableText } from "../portableText/PortableText";
import { Image, IImage } from "../elements/Image";
import { PortableTextBlock } from "@portabletext/types";
import { Heading } from "../elements/Heading";
// @ts-ignore
import GravityFormForm from "next-gravity-forms";

export type FooterProps = {
  data?: Menu;
  copyright?: Settings["copyright"];
  mailingList?: Settings["mailingList"];
  links?: Settings["footerLinks"];
  helpline?: Contact;
  email?: Contact;
  donateLink?: ILink;
  marqueeText?: string;
  socials?: Settings["socials"];
  foundingIndustryPartner?: Settings["foundingIndustryPartner"];
  mailingListForm?: any;
} & ComponentPropsWithRef<"div">;

export default function Footer({
  children,
  className,
  data,
  copyright,
  links,
  helpline,
  email,
  donateLink,
  marqueeText = "Stronger Together",
  socials,
  foundingIndustryPartner,
  mailingList,
  mailingListForm,
  ...props
}: FooterProps) {
  const foundingIndustryPartnerTextBlocks = (
    foundingIndustryPartner?.text as unknown as PortableTextBlock[] | undefined
  )?.map((block) => ({
    ...block,
    children: block.children ?? [],
  }));

  return (
    <footer className={styles.footerWrapper} {...props}>
      <div>
        <div className={styles.separatorWrapper}>
          <div className={styles.separatorWrappers} />
          <Exclude className={styles.exclude} />
          <div className={styles.separatorWrappers} />
        </div>

        <Grid className={styles.footerContent}>
          <div className={styles.footer}>
            <div className={styles.footerDetails}>
              <BrandMarkNorthAmerica className={styles.brandmark} />
              <div className={styles.divider}></div>
              <div className={styles.mailingList}>
                <div className={styles.mailingListContent}>
                  <Heading as="h1" size="h1">
                    {mailingList?.title}
                  </Heading>
                  <PortableText blocks={mailingList?.text as IPortableText} />
                </div>
                {mailingListForm && mailingListForm.data && (
                  <GravityFormForm {...mailingListForm} />
                )}
              </div>
              <div className={styles.divider}></div>
              <div className={styles.menuItemsGrid}>
                {data?.items?.map(
                  (item, index) =>
                    item.link &&
                    (item.link as any)?.slug && (
                      <div className={styles.items} key={index}>
                        <Link {...(item.link as any)} className={styles.link}>
                          <Paragraph size="lg" bold>
                            {item.link.text}
                          </Paragraph>
                        </Link>
                      </div>
                    ),
                )}
              </div>
            </div>

            <div className={styles.footerGrid}>
              <div className={styles.menuGrid}>
                {foundingIndustryPartner && (
                  <div className={styles.foundingIndustryPartner}>
                    <div className={styles.foundingIndustryPartnerContent}>
                      <Paragraph size="lg" bold={true}>
                        {foundingIndustryPartner?.title}
                      </Paragraph>
                      <PortableText
                        blocks={foundingIndustryPartnerTextBlocks}
                        className={styles.foundingIndustryPartnerText}
                      />
                    </div>
                    <div className={styles.foundingIndustryPartnerMedia}>
                      <Image
                        image={
                          foundingIndustryPartner?.image as unknown as IImage
                        }
                        className={styles.foundingIndustryPartnerImage}
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className={styles.buttons}>
                {helpline && (
                  <ButtonLink
                    link={helpline.link}
                    theme="colour"
                    icons={{ iconPrimary: <Phone /> }}
                  >
                    {helpline.link?.text}
                  </ButtonLink>
                )}
                {email && (
                  <ButtonLink
                    link={email.link}
                    theme="light"
                    icons={{ iconPrimary: <Send /> }}
                  >
                    {email.link?.text}
                  </ButtonLink>
                )}
              </div>
            </div>
          </div>

          <div className={styles.subfooter}>
            <Paragraph>{copyright}</Paragraph>
            {socials && <Socials theme="light" {...socials} />}
            <div className={styles.footerLinks}>
              {links?.map((link, index) => {
                return (
                  <Link {...link} key={index} className={styles.link}>
                    <Paragraph size="sm">{link.text}</Paragraph>
                  </Link>
                );
              })}
            </div>
          </div>
        </Grid>
      </div>
    </footer>
  );
}
